<template>
  <div ref="containerRef" :class="$style.container">
    <HgLink
      v-if="isSingleOption"
      icon="FloorPlan"
      tag="button"
      type="button"
      :class="$style.linkContainer"
      @click.stop="handleOptionClick(singleOption)"
    >
      {{ t('buttons.floorPlan') }}
    </HgLink>

    <template v-else>
      <HgLink
        icon="FloorPlan"
        tag="button"
        type="button"
        aria-haspopup="true"
        :aria-expanded="isOpen"
        :class="$style.linkContainer"
        @click.stop="toggle"
        @keydown.escape="close"
      >
        <span :class="$style.triggerLabel">
          {{ t('buttons.floorPlan') }}
          <HgIcon
            icon="ChevronDown"
            size="small"
            color="current"
            :class="$style.chevron"
            :rotation="isOpen ? 180 : 0"
          />
        </span>
      </HgLink>

      <div v-if="isOpen" :class="$style.menu" role="menu" :aria-label="t('buttons.floorPlan')">
        <HgButton
          v-for="option in floorPlanOptions"
          :key="option.id"
          type="button"
          theme="ghost"
          role="menuitem"
          :class="$style.menuItem"
          @click="handleOptionClick(option)"
        >
          {{ option.label }}
        </HgButton>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { HgButton, HgIcon, HgLink } from '@smg-real-estate/domus';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { DetectedFloorPlans } from '@/helpers/ListingData/ListingData';
import { getNumberedLabel } from '@/helpers/getNumberedLabel';
import { ListingDetailsModalTabId } from '@/models';
import { useListingImageGalleryStore } from '@/store/modules/listing-image-gallery';

defineOptions({
  name: 'FloorPlanSelector',
});

interface Props {
  floorPlans: DetectedFloorPlans;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  floorPlanClick: [];
}>();

const { t } = useI18n();
const listingImageGalleryStore = useListingImageGalleryStore();
const containerRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);

interface FloorPlanImageOption {
  id: string;
  type: 'image';
  imageIndex: number;
  label: string;
}

interface FloorPlanDocumentOption {
  id: string;
  type: 'document';
  href: string;
  label: string;
}

type FloorPlanOption = FloorPlanImageOption | FloorPlanDocumentOption;

const floorPlanOptions = computed((): FloorPlanOption[] => {
  const imageCount = props.floorPlans.images.length;
  const documentCount = props.floorPlans.documents.length;
  const imageLabel = t('floorPlan.imageLabel');
  const documentLabel = t('floorPlan.documentLabel');

  const imageOptions: FloorPlanImageOption[] = props.floorPlans.images.map((_, index) => ({
    id: `floor-plan-image-${index}`,
    type: 'image',
    imageIndex: index,
    label: getNumberedLabel(imageLabel, imageCount, index),
  }));

  const documentOptions: FloorPlanDocumentOption[] = props.floorPlans.documents.map(
    (document, index) => ({
      id: `floor-plan-document-${document.href}-${index}`,
      type: 'document',
      href: document.href || '',
      label: getNumberedLabel(documentLabel, documentCount, index),
    }),
  );

  return [...imageOptions, ...documentOptions];
});

const isSingleOption = computed((): boolean => floorPlanOptions.value.length === 1);

const singleOption = computed((): FloorPlanOption => floorPlanOptions.value[0]);

const toggle = (): void => {
  isOpen.value = !isOpen.value;
};

const close = (): void => {
  isOpen.value = false;
};

const openFloorPlanImage = (imageIndex: number): void => {
  listingImageGalleryStore.setActiveFloorPlanSlideIndex(imageIndex);
  listingImageGalleryStore.setSelectedModalTabId(ListingDetailsModalTabId.MODAL_TAB_ID_FLOOR_PLAN);
  listingImageGalleryStore.openModal();
};

const openFloorPlanDocument = (href: string): void => {
  window.open(href, '_blank', 'noopener,noreferrer');
};

const handleOptionClick = (option: FloorPlanOption): void => {
  if (option.type === 'image') {
    openFloorPlanImage(option.imageIndex);
  } else {
    openFloorPlanDocument(option.href);
  }

  emit('floorPlanClick');
  close();
};

const handleClickOutside = (event: MouseEvent): void => {
  if (containerRef.value && !containerRef.value.contains(event.target as Node)) {
    close();
  }
};

onMounted(() => {
  if (isSingleOption.value) return;

  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  if (isSingleOption.value) return;

  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.container {
  position: relative;
}

.linkContainer {
  margin-left: 0;
}

.triggerLabel {
  display: inline-flex;
  align-items: center;
  gap: $space-25;
}

.chevron {
  flex-shrink: 0;
}

.menu {
  position: absolute;
  top: calc(100% + #{$space-25});
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  min-width: 203px;
  padding: $space-50 0;
  background-color: $color-white;
  border-radius: $border-radius;
  box-shadow: $elevation-low;
}

.menuItem {
  @include body-2;

  justify-content: flex-start;
  border-radius: 0;
  padding: $space-25 $space-75;

  &:hover,
  &:focus-visible {
    background-color: $color-blue-0 !important;
    outline: none;
  }
}
</style>
